import { Link } from 'react-router-dom';
import { AlertTriangle, Clock, Info, ChevronRight } from 'lucide-react';
import {
  type Deadline,
  type Severity,
  bucketBySeverity,
  relativeDays,
  KIND_LABEL,
} from '../lib/deadlines';

interface Props {
  items: Deadline[];
  /** When true, hide the matter column (we're already inside one matter). */
  compact?: boolean;
  emptyMessage?: string;
}

export default function DeadlinesList({ items, compact = false, emptyMessage }: Props) {
  if (items.length === 0) {
    return (
      <div className="text-sm text-slate-500">
        {emptyMessage ?? 'No deadlines in the next 30 days.'}
      </div>
    );
  }

  const buckets = bucketBySeverity(items);

  return (
    <div className="space-y-5">
      <Bucket
        title="Critical: within 7 days or overdue"
        items={buckets.critical}
        severity="critical"
        compact={compact}
      />
      <Bucket
        title="Attention: 8 to 14 days"
        items={buckets.attention}
        severity="attention"
        compact={compact}
      />
      <Bucket
        title="Upcoming: 15 to 30 days"
        items={buckets.upcoming}
        severity="upcoming"
        compact={compact}
      />
    </div>
  );
}

function Bucket({
  title,
  items,
  severity,
  compact,
}: {
  title: string;
  items: Deadline[];
  severity: Severity;
  compact: boolean;
}) {
  if (items.length === 0) return null;
  const headerCls =
    severity === 'critical'
      ? 'text-red-700'
      : severity === 'attention'
      ? 'text-amber-700'
      : 'text-slate-600';
  const Icon = severity === 'critical' ? AlertTriangle : severity === 'attention' ? Clock : Info;
  return (
    <div>
      <h3 className={`text-xs uppercase tracking-wide font-semibold inline-flex items-center gap-1.5 mb-2 ${headerCls}`}>
        <Icon size={12} /> {title} <span className="text-slate-400">({items.length})</span>
      </h3>
      <ul className="space-y-1.5">
        {items.map((d, i) => (
          <DeadlineRow key={i} deadline={d} compact={compact} />
        ))}
      </ul>
    </div>
  );
}

function DeadlineRow({ deadline, compact }: { deadline: Deadline; compact: boolean }) {
  const sevDot =
    deadline.severity === 'critical'
      ? 'bg-red-500'
      : deadline.severity === 'attention'
      ? 'bg-amber-500'
      : 'bg-blue-400';

  const inner = (
    <div className="flex items-start gap-3 p-3 rounded-md hover:bg-slate-50 transition-colors">
      <span className={`mt-1.5 w-2 h-2 rounded-full ${sevDot} shrink-0`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-500">
            {KIND_LABEL[deadline.kind]}
          </span>
          <span className={`text-xs font-semibold ${
            deadline.daysAway < 0
              ? 'text-red-700'
              : deadline.severity === 'critical'
              ? 'text-red-700'
              : deadline.severity === 'attention'
              ? 'text-amber-700'
              : 'text-slate-600'
          }`}>
            {relativeDays(deadline.daysAway)}
          </span>
          <span className="text-xs text-slate-400">{formatDate(deadline.date)}</span>
        </div>
        <div className="text-sm font-semibold text-[#0B2341] truncate">
          {deadline.label}
        </div>
        <div className="text-xs text-slate-600 mt-0.5">
          {!compact && deadline.matterCode && (
            <span className="font-mono mr-2">{deadline.matterCode}</span>
          )}
          <span>{deadline.contactName}</span>
        </div>
        {deadline.detail && (
          <div className="text-xs text-slate-500 mt-0.5">{deadline.detail}</div>
        )}
      </div>
      {deadline.matterId && <ChevronRight size={14} className="text-slate-400 mt-2 shrink-0" />}
    </div>
  );

  if (deadline.matterId) {
    return (
      <li>
        <Link to={`/matters/${deadline.matterId}`} className="block">
          {inner}
        </Link>
      </li>
    );
  }
  return <li>{inner}</li>;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
